import {
  Drawer,
  Stack,
  Group,
  Text,
  Badge,
  Loader,
  Divider,
  Anchor,
  Code,
  ScrollArea,
  Alert,
  Paper,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { useCommunicationLog } from "../../../hooks/admin/useCommunicationLogs";
import {
  CommunicationChannel,
  CommunicationStatus,
} from "../../../services/admin/communicationLog.service";
import { LAB_TIME_ZONE } from "../../../utils/labTime";
import CommunicationRoutingBadge from "./CommunicationRoutingBadge";

const CHANNEL_COLORS: Record<CommunicationChannel, string> = {
  email: "blue",
  hl7: "violet",
  fax: "indigo",
  sms: "teal",
  voice: "grape",
};

const STATUS_COLORS: Record<CommunicationStatus, string> = {
  pending: "gray",
  sent: "blue",
  delivered: "green",
  failed: "red",
  bounced: "orange",
};

interface Props {
  logId: string | null;
  opened: boolean;
  onClose: () => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    timeZone: LAB_TIME_ZONE,
  });
}

function formatMatchedVia(value: string | null): string {
  if (!value) return "—";
  return value.replace(/_/g, " ");
}

export default function CommunicationLogDetailDrawer({
  logId,
  opened,
  onClose,
}: Props) {
  const { data: log, isLoading } = useCommunicationLog(logId);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Communication Detail"
      position="right"
      size="xl"
      padding="lg"
    >
      {isLoading || !log ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : (
        <Stack gap="md">
          <Group gap="xs">
            <Badge color={CHANNEL_COLORS[log.channel]} variant="light">
              {log.channel}
            </Badge>
            <Badge color={STATUS_COLORS[log.status]} variant="light">
              {log.status}
            </Badge>
            <CommunicationRoutingBadge decision={log.routingDecision} />
            <Text size="sm" c="dimmed">
              {log.communicationType}
            </Text>
          </Group>

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">
              Intended recipient
            </Text>
            <Text size="sm">{log.recipient}</Text>
          </Stack>

          <Divider label="Routing" labelPosition="left" />

          <Stack gap="xs">
            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Text size="sm" c="dimmed" w={140}>
                Actual destination
              </Text>
              <Text size="sm" ff="monospace" style={{ wordBreak: "break-all" }}>
                {log.actualRecipient || "—"}
              </Text>
            </Group>
            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Text size="sm" c="dimmed" w={140}>
                Routing reason
              </Text>
              <Text size="sm">{log.routingReason || "—"}</Text>
            </Group>
            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Text size="sm" c="dimmed" w={140}>
                Matched prefix
              </Text>
              <Text size="sm" ff="monospace">
                {log.matchedPrefix?.code || log.matchedPrefix?.id || "—"}
              </Text>
            </Group>
            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Text size="sm" c="dimmed" w={140}>
                Matched via
              </Text>
              <Text size="sm" tt="capitalize">
                {formatMatchedVia(log.matchedVia)}
              </Text>
            </Group>
          </Stack>

          {log.subject && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase">
                Subject
              </Text>
              <Text size="sm">{log.subject}</Text>
            </Stack>
          )}

          <Group grow align="flex-start">
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase">
                Sent At
              </Text>
              <Text size="sm">{formatDate(log.sentAt)}</Text>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase">
                Delivered At
              </Text>
              <Text size="sm">{formatDate(log.deliveredAt)}</Text>
            </Stack>
          </Group>

          {log.failureReason && (
            <Alert
              icon={<IconAlertTriangle size={18} />}
              color="red"
              variant="light"
              title="Delivery failed"
              role="alert"
            >
              {log.failureReason}
            </Alert>
          )}

          <Divider label="Linked Records" labelPosition="left" />

          <Stack gap="xs">
            {log.case && (
              <Group gap="xs">
                <Text size="sm" c="dimmed" w={120}>
                  Case
                </Text>
                <Anchor component={Link} to={`/cases/${log.case.id}`} size="sm">
                  {log.case.caseNumber || log.case.id}
                </Anchor>
              </Group>
            )}
            {log.patient && (
              <Group gap="xs">
                <Text size="sm" c="dimmed" w={120}>
                  Patient
                </Text>
                <Anchor
                  component={Link}
                  to={`/patients/${log.patient.id}`}
                  size="sm"
                >
                  {log.patient.name}
                </Anchor>
              </Group>
            )}
            {log.recipientUser && (
              <Group gap="xs">
                <Text size="sm" c="dimmed" w={120}>
                  Recipient User
                </Text>
                <Anchor
                  component={Link}
                  to={`/admin/users/${log.recipientUser.id}`}
                  size="sm"
                >
                  {log.recipientUser.name}
                </Anchor>
              </Group>
            )}
            {log.triggeredBy && (
              <Group gap="xs">
                <Text size="sm" c="dimmed" w={120}>
                  Triggered By
                </Text>
                <Text size="sm">{log.triggeredBy.name}</Text>
              </Group>
            )}
            {log.providerMessageId && (
              <Group gap="xs">
                <Text size="sm" c="dimmed" w={120}>
                  Provider ID
                </Text>
                <Code>{log.providerMessageId}</Code>
              </Group>
            )}
          </Stack>

          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <>
              <Divider label="Metadata" labelPosition="left" />
              <Code block>{JSON.stringify(log.metadata, null, 2)}</Code>
            </>
          )}

          {log.body && (
            <>
              <Divider label="Body" labelPosition="left" />
              <ScrollArea.Autosize mah={400}>
                {log.channel === "email" ? (
                  <Paper
                    p="md"
                    withBorder
                    bg="var(--mantine-color-body)"
                    // Body comes from backend either masked or unmasked depending on
                    // the viewer's permissions. Frontend never decides masking.
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: log.body }}
                  />
                ) : (
                  <Code block>{log.body}</Code>
                )}
              </ScrollArea.Autosize>
            </>
          )}
        </Stack>
      )}
    </Drawer>
  );
}
